import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplaceGoogleSlidesHistory.graphql';
import { Query, QuerymarketplaceGoogleSlidesHistoryArgs } from '../../__generated__/globalTypes';

export const useMarketplaceGoogleSlidesHistoryQuery = (
  options?: QueryHookOptions<Query, QuerymarketplaceGoogleSlidesHistoryArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplaceGoogleSlidesHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplaceGoogleSlidesHistoryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplaceGoogleSlidesHistoryArgs>
): QueryResult<Query, QuerymarketplaceGoogleSlidesHistoryArgs> & {
  getMarketplaceGoogleSlidesHistory: LazyQueryExecFunction<Query, QuerymarketplaceGoogleSlidesHistoryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplaceGoogleSlidesHistory, result] = useLazyQuery<Query, QuerymarketplaceGoogleSlidesHistoryArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getMarketplaceGoogleSlidesHistory, ...result };
};

export const useMarketplaceGoogleSlidesHistoryPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplaceGoogleSlidesHistory = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplaceGoogleSlidesHistoryArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplaceGoogleSlidesHistoryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplaceGoogleSlidesHistory };
};
// Suspense Query Hooks
export const useMarketplaceGoogleSlidesHistorySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplaceGoogleSlidesHistoryArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplaceGoogleSlidesHistoryArgs>(QUERY, {
    ...options
  });

  return result;
};
